import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getDollarRate, updateDollarRate } from "../../api/Settings/DollarRate";
import Spinner from "../../components/Shared/Spinner";
import { queryClient } from "../../main";

const Currency = () => {
  const [inputValue, setInputValue] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["dollar-rate"],
    queryFn: getDollarRate,
  });

  useEffect(() => {
    if (data?.dollarRate !== undefined) {
      setInputValue(String(data.dollarRate));
    }
  }, [data?.dollarRate]);

  const { mutate, isPending } = useMutation({
    mutationFn: () => updateDollarRate(Number(inputValue)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dollar-rate"] });
      toast.success("Dollar rate updated successfully");
    },
    onError: () => {
      toast.error("Failed to update dollar rate");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = Number(inputValue);
    if (isNaN(parsed) || parsed <= 0) {
      toast.error("Please enter a valid positive number");
      return;
    }
    mutate();
  };

  return (
    <div className="py-7">
      <h1 className="text-burntBlack font-bold text-[32px] mb-2">Currency Settings</h1>

      <div className="max-w-lg flex flex-col gap-5">
        {/* Current rate card */}
        <div className="rounded-xl bg-white border border-black/10 shadow p-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Current Exchange Rate</p>

          {isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : (
            <div className="flex items-center gap-4">
              {/* USD side */}
              <div className="flex flex-col items-center justify-center bg-green-50 border border-green-200 rounded-xl w-28 h-20">
                <span className="text-2xl font-extrabold text-green-600">$1</span>
                <span className="text-xs text-green-500 font-medium mt-0.5">USD</span>
              </div>

              {/* equals */}
              <div className="flex flex-col items-center gap-1 text-gray-400">
                <div className="w-8 h-px bg-gray-300" />
                <span className="text-lg font-bold">=</span>
                <div className="w-8 h-px bg-gray-300" />
              </div>

              {/* EGP side */}
              <div className="flex flex-col items-center justify-center bg-blue-50 border border-blue-200 rounded-xl flex-1 h-20">
                <span className="text-3xl font-extrabold text-blue-600">
                  {data?.dollarRate ?? "—"}
                </span>
                <span className="text-xs text-blue-500 font-medium mt-0.5">EGP</span>
              </div>
            </div>
          )}
        </div>

        {/* Update form card */}
        {!isLoading && (
          <div className="rounded-xl bg-white border border-black/10 shadow p-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4">Update Rate</p>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-semibold text-gray-500 whitespace-nowrap">
                    <span>$1 USD =</span>
                  </div>
                  <div className="relative flex-1">
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="e.g. 54"
                      className="w-full border border-gray-300 rounded-lg pl-4 pr-16 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">EGP</span>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isPending || !inputValue}
                className="h-11 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg text-sm transition-colors"
              >
                {isPending ? "Saving..." : "Save Rate"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Currency;
